

















import { formatCurrency } from '@getaccept/lib-shared/src/helpers/currency';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Currency } from '@getaccept/lib-shared/src/types/Currency';
import type { PropType, Ref } from '@vue/composition-api';
import { computed, defineComponent, ref, toRefs, watch } from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import type { DocumentStats } from '../../api/types/document-stats';
import { DashboardService } from '../../api/service/dashboard.service';
import { DashboardWidgetHeaderColor } from '../../../types/enums/dashboard-widget-header-color';
import { DashboardWidgetValueColor } from '../../../types/enums/dashboard-widget-value-color';
import DashboardWidget from '../../../components/DashboardWidget.vue';
import { useWidgetStore } from '../store/widget.store';
import { DocumentStatsType } from '../../api/types/document-stats-type';
import { useLoadDocumentStats } from '../composables/load-document-stats.composable';

export default defineComponent({
  name: 'EstimatedSalesWidget',
  components: {
    DashboardWidget,
  },
  props: {
    currency: {
      type: Object as PropType<Currency>,
      required: true,
    },
    showAll: {
      type: Boolean,
    },
  },
  setup(props) {
    const { averageDealCloseDays, wonValue } = storeToRefs(useWidgetStore());

    const loadingEstimatedData = ref(false);
    const loadedEstimatedData = ref(false);
    const error = ref(false);
    const estimatedData: Ref<DocumentStats> = ref(null);

    const { showAll } = toRefs(props);

    const {
      isLoading: statsLoading,
      isSuccess: statsLoaded,
      documentCount,
      documentValue,
    } = useLoadDocumentStats(DocumentStatsType.HitTotal, showAll);

    const load = async () => {
      loadingEstimatedData.value = true;
      loadedEstimatedData.value = false;
      error.value = false;
      try {
        estimatedData.value = await DashboardService.loadEstimateClosingDeals(
          averageDealCloseDays.value,
          props.showAll
        );
        loadedEstimatedData.value = true;
      } catch (e) {
        error.value = true;
      } finally {
        loadingEstimatedData.value = false;
      }
    };

    const title = computed(() => (estimatedData.value?.count === 1 ? t('Deal') : t('Deals')));
    const count = computed(() => estimatedData.value?.count || 0);

    const loadData = () => {
      if (averageDealCloseDays.value === null) {
        return;
      }
      load();
    };

    watch(
      averageDealCloseDays,
      (newValue: number, oldValue: number) => {
        if (newValue !== oldValue) {
          loadData();
        }
      },
      { immediate: true }
    );

    watch(showAll, loadData);

    const calculatedValue = computed(() => {
      if (!estimatedData.value || !documentCount.value || !documentValue.value) {
        return 0;
      }
      return (
        wonValue.value + estimatedData.value.value * (documentCount.value / documentValue.value)
      );
    });

    const formattedValue = computed(() =>
      formatCurrency(Math.round(calculatedValue.value), props.currency)
    );

    return {
      title,
      formattedValue,
      DashboardWidgetHeaderColor,
      DashboardWidgetValueColor,
      count,
      loading: computed(() => loadingEstimatedData.value || statsLoading.value),
      loaded: computed(() => loadedEstimatedData.value || statsLoaded.value),
    };
  },
});
